import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "../../App";
import DashboardLayout from "../../components/DashboardLayout";
import DealReviewModal from "../../components/DealReviewModal";
import InterestedDealsSection from "../../components/InterestedDealsSection";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Building2,
  Search,
  Filter,
  Eye,
  CheckCircle,
  XCircle,
  FileText,
  RefreshCw,
  UserPlus,
  UserMinus,
  ChevronLeft,
  ChevronRight,
  MapPin,
  X,
  ExternalLink,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";

const DEAL_TYPE_LABELS = {
  flip: "Flip",
  rental: "Rental",
  development: "Development",
};

const DEAL_TYPE_COLORS = {
  flip: "bg-orange-500/10 text-orange-500",
  rental: "bg-blue-500/10 text-blue-500",
  development: "bg-purple-500/10 text-purple-500",
};

const STATUS_LABELS = {
  pending: "Pending Review",
  active: "Live",
  approved: "Approved",
  reserved: "Under Contract",
  in_closing: "In Closing",
  closed: "Closed",
  rejected: "Rejected",
  changes_requested: "Changes Requested",
  removed: "Removed",
};

const STATUS_COLORS = {
  pending: "bg-orange-500/10 text-orange-500 border-orange-500/20",
  active: "bg-green-500/10 text-green-500 border-green-500/20",
  approved: "bg-green-500/10 text-green-500 border-green-500/20",
  reserved: "bg-blue-500/10 text-blue-500 border-blue-500/20",
  in_closing: "bg-purple-500/10 text-purple-500 border-purple-500/20",
  closed: "bg-gray-500/10 text-gray-500 border-gray-500/20",
  rejected: "bg-red-500/10 text-red-500 border-red-500/20",
  changes_requested: "bg-yellow-500/10 text-yellow-600 border-yellow-500/20",
  removed: "bg-gray-500/10 text-gray-400 border-gray-500/20",
};

const ITEMS_PER_PAGE = 10;

const LiveDealsPage = () => {
  const [searchParams] = useSearchParams();
  const urlStatus = searchParams.get("status");

  // Map URL param to internal filter value
  const getInitialFilter = () => {
    const map = {
      pending_review: "pending",
      live: "live",
      interested: "interested",
      under_contract: "under_contract",
      closed: "closed",
    };
    return map[urlStatus] || "all";
  };

  const getPageTitle = () => {
    const map = {
      pending_review: "Pending Review",
      live: "Live Deals",
      interested: "Interested Deals",
      under_contract: "Under Contract",
      closed: "Deals Closed",
    };
    return map[urlStatus] || "All Deals";
  };

  const [deals, setDeals] = useState([]);
  const [investors, setInvestors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [dealToAssign, setDealToAssign] = useState(null);
  const [selectedInvestorId, setSelectedInvestorId] = useState("");
  const [assignmentNotes, setAssignmentNotes] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [replaceNda, setReplaceNda] = useState(false);
  const [replaceNdaFile, setReplaceNdaFile] = useState(null);
  const [dealNdaUrl, setDealNdaUrl] = useState(null);
  const [statusFilter, setStatusFilter] = useState(getInitialFilter);
  const [stateFilter, setStateFilter] = useState("all");
  const [cityFilter, setCityFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  // Sync filter when URL changes
  useEffect(() => {
    setStatusFilter(getInitialFilter());
    setCurrentPage(1);
  }, [urlStatus]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("auth_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchDeals = async () => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${API}/admin/deals`, {
        withCredentials: true,
        headers,
      });
      setDeals(response.data);
    } catch (error) {
      console.error("Failed to fetch deals:", error);
      toast.error("Failed to fetch deals");
    } finally {
      setLoading(false);
    }
  };

  const fetchInvestors = async () => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${API}/admin/investors`, {
        withCredentials: true,
        headers,
      });
      setInvestors(response.data.filter((inv) => inv.is_active !== false));
    } catch (error) {
      console.error("Failed to fetch investors:", error);
    }
  };

  const fetchDealNdaUrl = async () => {
    try {
      const res = await axios.get(`${API}/settings`, {
        withCredentials: true,
        headers: getAuthHeaders(),
      });
      if (res.data?.deal_nda) setDealNdaUrl(res.data.deal_nda);
    } catch {}
  };

  useEffect(() => {
    fetchDeals();
    fetchInvestors();
    fetchDealNdaUrl();
  }, []);

  // Get unique states and cities for filters
  const availableStates = useMemo(() => {
    const states = [...new Set(deals.map((d) => d.state).filter(Boolean))];
    return states.sort();
  }, [deals]);

  const availableCities = useMemo(() => {
    let filteredDeals = deals;
    if (stateFilter !== "all") {
      filteredDeals = deals.filter((d) => d.state === stateFilter);
    }
    const cities = [
      ...new Set(filteredDeals.map((d) => d.city).filter(Boolean)),
    ];
    return cities.sort();
  }, [deals, stateFilter]);

  const handleReviewDeal = (deal) => {
    setSelectedDeal(deal);
    setShowReviewModal(true);
  };

  const handleDealUpdated = () => {
    fetchDeals();
    setShowReviewModal(false);
    setSelectedDeal(null);
  };

  const handleDealStatusChange = async (dealId, newStatus, reason = null) => {
    try {
      const headers = getAuthHeaders();
      await axios.post(
        `${API}/admin/deals/${dealId}/status`,
        { new_status: newStatus, reason },
        { withCredentials: true, headers },
      );
      toast.success(`Deal status updated to ${newStatus}`);
      fetchDeals();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Failed to update deal status",
      );
    }
  };

  const openAssignModal = (deal) => {
    setDealToAssign(deal);
    setSelectedInvestorId("");
    setAssignmentNotes("");
    setReplaceNda(false);
    setReplaceNdaFile(null);
    setShowAssignModal(true);
  };

  const handleAssignDeal = async () => {
    if (!selectedInvestorId) {
      toast.error("Please select an investor");
      return;
    }
    if (replaceNda && !replaceNdaFile) {
      toast.error("Please select a PDF file to attach or uncheck the option");
      return;
    }

    setAssigning(true);
    try {
      const headers = getAuthHeaders();

      // If admin chose a replacement NDA, upload it first then attach after assignment
      let replacementKey = null;
      if (replaceNda && replaceNdaFile) {
        const presign = await axios.post(
          `${API}/uploads/presign`,
          {
            filename: replaceNdaFile.name,
            content_type: "application/pdf",
            category: "nda",
          },
          { withCredentials: true, headers },
        );
        const { upload_url, fields, storage_path } = presign.data;
        const fd = new FormData();
        Object.entries(fields).forEach(([k, v]) => fd.append(k, v));
        fd.append("file", replaceNdaFile);
        await axios.post(upload_url, fd);
        replacementKey = storage_path;
      }

      await axios.post(
        `${API}/admin/deals/${dealToAssign.deal_id}/assign`,
        {
          investor_id: selectedInvestorId,
          notes: assignmentNotes,
          nda_s3_key: replacementKey || undefined,
        },
        { withCredentials: true, headers },
      );

      const investor = investors.find(
        (inv) => inv.user_id === selectedInvestorId,
      );
      toast.success(`Deal assigned to ${investor?.name || "investor"}`);
      setShowAssignModal(false);
      setDealToAssign(null);
      fetchDeals();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign deal");
    } finally {
      setAssigning(false);
    }
  };

  const [unassignTarget, setUnassignTarget] = useState(null); // { dealId, investorName }

  const handleUnassignDeal = async () => {
    if (!unassignTarget) return;
    const { dealId, investorName } = unassignTarget;
    try {
      const headers = getAuthHeaders();
      await axios.post(
        `${API}/admin/deals/${dealId}/unassign`,
        {},
        { withCredentials: true, headers },
      );
      toast.success(`Deal unassigned from ${investorName}`);
      setUnassignTarget(null);
      fetchDeals();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to unassign deal");
    }
  };

  const filteredDeals = useMemo(() => {
    let result = [...deals];

    // Status filter
    if (statusFilter !== "all") {
      if (statusFilter === "pending") {
        result = result.filter((d) => d.status === "pending");
      } else if (statusFilter === "live") {
        result = result.filter((d) =>
          ["active", "approved"].includes(d.status),
        );
      } else if (statusFilter === "under_contract") {
        result = result.filter((d) =>
          ["reserved", "in_closing"].includes(d.status),
        );
      } else if (statusFilter === "closed") {
        result = result.filter((d) => d.status === "closed");
      } else if (statusFilter === "rejected") {
        result = result.filter((d) => d.status === "rejected");
      }
    }

    // State filter
    if (stateFilter !== "all") {
      result = result.filter((d) => d.state === stateFilter);
    }

    // City filter
    if (cityFilter !== "all") {
      result = result.filter((d) => d.city === cityFilter);
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (d) =>
          d.property_address?.toLowerCase().includes(query) ||
          d.city?.toLowerCase().includes(query) ||
          d.submitted_by_name?.toLowerCase().includes(query) ||
          d.reserved_by_name?.toLowerCase().includes(query),
      );
    }

    return result;
  }, [deals, statusFilter, stateFilter, cityFilter, searchQuery]);

  // Pagination
  const totalPages = Math.ceil(filteredDeals.length / ITEMS_PER_PAGE);
  const paginatedDeals = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredDeals.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredDeals, currentPage]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, stateFilter, cityFilter, searchQuery]);

  // Reset city filter when state changes
  useEffect(() => {
    setCityFilter("all");
  }, [stateFilter]);

  const pendingDeals = useMemo(() => {
    return deals
      .filter((d) => d.status === "pending")
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [deals]);

  const liveDeals = useMemo(() => {
    return deals.filter((d) =>
      ["active", "approved", "reserved", "in_closing"].includes(d.status),
    );
  }, [deals]);

  const formatCurrency = (value) => {
    if (!value) return "$0";
    return "$" + value.toLocaleString();
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const clearFilters = () => {
    setSearchQuery("");
    setStatusFilter("all");
    setStateFilter("all");
    setCityFilter("all");
  };

  const hasActiveFilters =
    searchQuery ||
    statusFilter !== "all" ||
    stateFilter !== "all" ||
    cityFilter !== "all";

  // Dedicated full-page mode for the "Interested Deals" sidebar entry
  if (urlStatus === "interested") {
    return (
      <DashboardLayout title="Interested Deals">
        <div
          className="space-y-6 animate-fade-in"
          data-testid="interested-deals-page"
        >
          <InterestedDealsSection onDealAssigned={fetchDeals} />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <>
      <DashboardLayout title={getPageTitle()}>
        <div
          className="space-y-6 animate-fade-in"
          data-testid="live-deals-page"
        >
          {/* Work Queue - Deals Requiring Action */}
          {pendingDeals.length > 0 && (
            <Card className="border-orange-500/50">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-orange-500 animate-pulse" />
                    <CardTitle className="text-lg font-heading text-orange-600 dark:text-orange-400">
                      Deals Requiring Action
                    </CardTitle>
                    <Badge className="bg-orange-500 text-white">
                      {pendingDeals.length}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="pending-deals-table">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase">
                          Property
                        </th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase">
                          Submitted By
                        </th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase">
                          Type
                        </th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase">
                          Price
                        </th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase">
                          Date
                        </th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingDeals.slice(0, 5).map((deal) => (
                        <tr
                          key={deal.deal_id}
                          className="border-b hover:bg-muted/50 transition-colors"
                        >
                          <td className="py-3 px-4">
                            <div>
                              <p className="font-medium text-foreground">
                                {deal.property_address}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                {deal.city}, {deal.state}
                              </p>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <p className="text-sm">
                              {deal.submitted_by_name || "Unknown"}
                            </p>
                          </td>
                          <td className="py-3 px-4">
                            <Badge className={DEAL_TYPE_COLORS[deal.deal_type]}>
                              {DEAL_TYPE_LABELS[deal.deal_type] ||
                                deal.deal_type}
                            </Badge>
                          </td>
                          <td className="py-3 px-4">
                            <p className="font-medium">
                              {formatCurrency(deal.asking_price)}
                            </p>
                          </td>
                          <td className="py-3 px-4">
                            <p className="text-sm text-muted-foreground">
                              {formatDate(deal.created_at)}
                            </p>
                          </td>
                          <td className="py-3 px-4">
                            <Button
                              size="sm"
                              onClick={() => handleReviewDeal(deal)}
                              data-testid={`review-deal-${deal.deal_id}`}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              Review
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Live Deal Management */}
          <Card>
            <CardHeader>
              <div className="flex flex-col gap-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <CardTitle className="font-heading">All Deals</CardTitle>
                    <Badge variant="outline">
                      {filteredDeals.length} Total
                    </Badge>
                  </div>
                </div>

                {/* Filters */}
                <div className="flex flex-wrap items-center gap-3">
                  <div className="relative flex-1 min-w-[180px] max-w-[250px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search deals..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9"
                      data-testid="search-deals-input"
                    />
                  </div>

                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger
                      className="w-[160px]"
                      data-testid="status-filter"
                    >
                      <Filter className="w-4 h-4 mr-2" />
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="pending">Pending Review</SelectItem>
                      <SelectItem value="live">Live Deals</SelectItem>
                      <SelectItem value="under_contract">
                        Under Contract
                      </SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select value={stateFilter} onValueChange={setStateFilter}>
                    <SelectTrigger
                      className="w-[160px]"
                      data-testid="state-filter"
                    >
                      <MapPin className="w-4 h-4 mr-2" />
                      <SelectValue placeholder="State" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All States</SelectItem>
                      {availableStates.map((state) => (
                        <SelectItem key={state} value={state}>
                          {state}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select
                    value={cityFilter}
                    onValueChange={setCityFilter}
                    disabled={stateFilter === "all"}
                  >
                    <SelectTrigger
                      className="w-[160px]"
                      data-testid="city-filter"
                    >
                      <Building2 className="w-4 h-4 mr-2" />
                      <SelectValue placeholder="City" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Cities</SelectItem>
                      {availableCities.map((city) => (
                        <SelectItem key={city} value={city}>
                          {city}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {hasActiveFilters && (
                    <Button variant="ghost" size="sm" onClick={clearFilters}>
                      <X className="w-4 h-4 mr-1" />
                      Clear
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="loading-spinner border-blue-600"></div>
                </div>
              ) : paginatedDeals.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No deals found</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="all-deals-table">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase">
                            Property
                          </th>
                          <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase">
                            Type
                          </th>
                          <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase">
                            Price
                          </th>
                          <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase">
                            Status
                          </th>
                          <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase">
                            Assigned To
                          </th>
                          <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedDeals.map((deal) => (
                          <tr
                            key={deal.deal_id}
                            className="border-b hover:bg-muted/50 transition-colors"
                          >
                            <td className="py-3 px-4">
                              <p className="font-medium">
                                {deal.property_address}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                {deal.city}, {deal.state}
                              </p>
                            </td>
                            <td className="py-3 px-4">
                              <Badge
                                className={DEAL_TYPE_COLORS[deal.deal_type]}
                              >
                                {DEAL_TYPE_LABELS[deal.deal_type]}
                              </Badge>
                            </td>
                            <td className="py-3 px-4">
                              <p className="font-medium">
                                {formatCurrency(deal.asking_price)}
                              </p>
                            </td>
                            <td className="py-3 px-4">
                              <Badge
                                variant="outline"
                                className={STATUS_COLORS[deal.status]}
                              >
                                {STATUS_LABELS[deal.status]}
                              </Badge>
                            </td>
                            <td className="py-3 px-4">
                              {deal.reserved_by_name ? (
                                <div>
                                  <p className="text-sm font-medium">
                                    {deal.reserved_by_name}
                                  </p>
                                  {deal.assigned_by_admin && (
                                    <p className="text-xs text-muted-foreground">
                                      Assigned by admin
                                    </p>
                                  )}
                                </div>
                              ) : (
                                <p className="text-sm text-muted-foreground">
                                  Not assigned
                                </p>
                              )}
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleReviewDeal(deal)}
                                  title="View Details"
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>

                                {/* Assign/Unassign buttons */}
                                {!deal.reserved_by &&
                                  ["active", "approved"].includes(
                                    deal.status,
                                  ) && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => openAssignModal(deal)}
                                      title="Assign to Investor"
                                      data-testid={`assign-deal-${deal.deal_id}`}
                                    >
                                      <UserPlus className="w-4 h-4 text-blue-500" />
                                    </Button>
                                  )}

                                {deal.reserved_by &&
                                  ["reserved", "active"].includes(
                                    deal.status,
                                  ) && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() =>
                                        setUnassignTarget({
                                          dealId: deal.deal_id,
                                          investorName: deal.reserved_by_name,
                                        })
                                      }
                                      title="Unassign from Investor"
                                      data-testid={`unassign-deal-${deal.deal_id}`}
                                    >
                                      <UserMinus className="w-4 h-4 text-red-500" />
                                    </Button>
                                  )}

                                {deal.status === "active" &&
                                  deal.reserved_by && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() =>
                                        handleDealStatusChange(
                                          deal.deal_id,
                                          "reserved",
                                        )
                                      }
                                      title="Mark Under Contract"
                                    >
                                      <FileText className="w-4 h-4" />
                                    </Button>
                                  )}
                                {(deal.status === "reserved" ||
                                  deal.status === "in_closing") && (
                                  <>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() =>
                                        handleDealStatusChange(
                                          deal.deal_id,
                                          "closed",
                                        )
                                      }
                                      title="Mark Closed"
                                    >
                                      <CheckCircle className="w-4 h-4 text-green-500" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() =>
                                        handleDealStatusChange(
                                          deal.deal_id,
                                          "reopened",
                                          "Investor failed to complete",
                                        )
                                      }
                                      title="Reopen Deal"
                                    >
                                      <RefreshCw className="w-4 h-4 text-orange-500" />
                                    </Button>
                                  </>
                                )}
                                {deal.status === "active" &&
                                  !deal.reserved_by && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() =>
                                        handleDealStatusChange(
                                          deal.deal_id,
                                          "removed",
                                          "Removed by admin",
                                        )
                                      }
                                      title="Remove from Marketplace"
                                    >
                                      <XCircle className="w-4 h-4 text-red-500" />
                                    </Button>
                                  )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between mt-4 pt-4 border-t">
                      <p className="text-sm text-muted-foreground">
                        Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1} to{" "}
                        {Math.min(
                          currentPage * ITEMS_PER_PAGE,
                          filteredDeals.length,
                        )}{" "}
                        of {filteredDeals.length} deals
                      </p>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setCurrentPage((p) => Math.max(1, p - 1))
                          }
                          disabled={currentPage === 1}
                          data-testid="prev-page"
                        >
                          <ChevronLeft className="w-4 h-4" />
                          Previous
                        </Button>
                        <div className="flex items-center gap-1">
                          {Array.from(
                            { length: Math.min(5, totalPages) },
                            (_, i) => {
                              let pageNum;
                              if (totalPages <= 5) {
                                pageNum = i + 1;
                              } else if (currentPage <= 3) {
                                pageNum = i + 1;
                              } else if (currentPage >= totalPages - 2) {
                                pageNum = totalPages - 4 + i;
                              } else {
                                pageNum = currentPage - 2 + i;
                              }
                              return (
                                <Button
                                  key={pageNum}
                                  variant={
                                    currentPage === pageNum
                                      ? "default"
                                      : "outline"
                                  }
                                  size="sm"
                                  className="w-8 h-8 p-0"
                                  onClick={() => setCurrentPage(pageNum)}
                                >
                                  {pageNum}
                                </Button>
                              );
                            },
                          )}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setCurrentPage((p) => Math.min(totalPages, p + 1))
                          }
                          disabled={currentPage === totalPages}
                          data-testid="next-page"
                        >
                          Next
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Deal Review Modal */}
        <DealReviewModal
          deal={selectedDeal}
          open={showReviewModal}
          onOpenChange={setShowReviewModal}
          onDealUpdated={handleDealUpdated}
        />

        {/* Assign Deal Modal */}
        <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserPlus className="w-5 h-5" />
                Assign Deal to Investor
              </DialogTitle>
              <DialogDescription>
                {dealToAssign && (
                  <span className="font-medium text-foreground">
                    {dealToAssign.property_address}, {dealToAssign.city}
                  </span>
                )}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Select Investor *</Label>
                <Select
                  value={selectedInvestorId}
                  onValueChange={setSelectedInvestorId}
                >
                  <SelectTrigger data-testid="select-investor">
                    <SelectValue placeholder="Choose an investor" />
                  </SelectTrigger>
                  <SelectContent>
                    {investors.map((investor) => (
                      <SelectItem
                        key={investor.user_id}
                        value={investor.user_id}
                      >
                        <div className="flex flex-col">
                          <span>{investor.name}</span>
                          <span className="text-xs text-muted-foreground">
                            {investor.email} • {investor.state}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Assignment Notes (optional)</Label>
                <Textarea
                  placeholder="Add notes about this assignment..."
                  value={assignmentNotes}
                  onChange={(e) => setAssignmentNotes(e.target.value)}
                  rows={3}
                  data-testid="assignment-notes"
                />
              </div>

              <div className="p-3 rounded-lg bg-blue-500/10 text-sm">
                <p className="text-blue-600 dark:text-blue-400">
                  <strong>Note:</strong> Assigning a deal will automatically:
                </p>
                <ul className="list-disc list-inside mt-1 text-muted-foreground">
                  <li>Reserve the deal for the selected investor</li>
                  <li>Sign the NDA on behalf of the investor</li>
                  <li>Send a notification to the investor</li>
                </ul>
              </div>

              {/* {dealNdaUrl ? (
              <div className="flex items-start gap-3 p-3 rounded-lg border border-orange-500/30 bg-orange-500/5">
                <FileText className="w-4 h-4 text-orange-500 shrink-0 mt-0.5" />
                <div className="text-sm space-y-2 flex-1">
                  <p className="font-medium text-orange-600 dark:text-orange-400">NDA will be attached to this assignment</p>
                  <p className="text-muted-foreground">
                    Please review the document before proceeding.{" "}
                    <a href={dealNdaUrl} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline inline-flex items-center gap-1">
                      View Deal NDA <ExternalLink className="w-3 h-3" />
                    </a>
                  </p>
                  <label className="flex items-center gap-2 cursor-pointer text-muted-foreground hover:text-foreground">
                    <input
                      type="checkbox"
                      checked={replaceNda}
                      onChange={(e) => { setReplaceNda(e.target.checked); setReplaceNdaFile(null); }}
                      className="rounded"
                    />
                    Attach a different NDA for this assignment instead
                  </label>
                  {replaceNda && (
                    <div className="space-y-2 pt-1">
                      <input
                        type="file"
                        accept=".pdf"
                        className="text-xs text-muted-foreground w-full"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (!f) return;
                          if (f.type !== "application/pdf") { toast.error("PDF files only"); return; }
                          if (f.size > 25 * 1024 * 1024) { toast.error("Max 25 MB"); return; }
                          setReplaceNdaFile(f);
                        }}
                      />
                      {replaceNdaFile && (
                        <p className="text-xs text-green-600">✓ {replaceNdaFile.name} ready to attach</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-3 p-3 rounded-lg border border-yellow-500/30 bg-yellow-500/5">
                <AlertCircle className="w-4 h-4 text-yellow-500 shrink-0 mt-0.5" />
                <p className="text-sm text-yellow-600 dark:text-yellow-400">No Deal NDA uploaded yet. Upload one in the NDA Documents settings before assigning.</p>
              </div>
            )} */}
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowAssignModal(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAssignDeal}
                disabled={!selectedInvestorId || assigning}
                data-testid="confirm-assign"
              >
                {assigning ? "Assigning..." : "Assign Deal"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DashboardLayout>

      {/* Unassign Confirmation Dialog */}
      <Dialog
        open={!!unassignTarget}
        onOpenChange={(open) => !open && setUnassignTarget(null)}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserMinus className="w-5 h-5 text-red-500" />
              Unassign Deal
            </DialogTitle>
            <DialogDescription>
              Remove this deal from{" "}
              <span className="font-medium text-foreground">
                {unassignTarget?.investorName}
              </span>
              ? The deal will return to the marketplace.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setUnassignTarget(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleUnassignDeal}>
              Unassign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LiveDealsPage;
